import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { MoreHorizontal } from 'lucide-react';
import {
  copyToClipboard,
  useCanTakePayments,
  useCreatePaymentLink,
  useDiscardInvoice,
  useInvoice,
  useSendFollowUp,
  useSendInvoice,
  useSetInvoiceStatus
} from '@/hooks/useInvoices';
import { useCompanyInfo } from '@/hooks/useSettings';
import { useBillingNouns } from '@/hooks/useSession';
import { creditFeeAmount, emailNotConfigured } from '@/api/invoices';
import { money, moneyCompact } from '@/lib/format';
import type { InvoiceDetail } from '@/types';
import { Screen } from '@m/ui/Screen';
import { Card, Group, Row } from '@m/ui/List';
import { BarButton, Button, Skeleton, SkeletonList } from '@m/ui/Controls';
import { ActionSheet, type SheetAction } from '@m/ui/Sheet';
import { StatusPill, isOverdue } from '@m/ui/StatusPill';
import { shortDay } from './Invoices';
import { NewInvoiceSheet } from './NewInvoice';
import { EditInvoiceSheet } from './invoices/EditInvoiceSheet';
import { shareInvoicePdf, viewInvoicePdf } from './invoices/pdf';

const FIRST = 5;

/** Hours to one decimal, as the canvas prints them: 6.0 h. */
export const h1 = (v: number) => (Math.round(v * 10) / 10).toFixed(1);

function statusLabel(inv: InvoiceDetail, overdue: boolean) {
  if (overdue) return `Overdue · due ${shortDay(inv.dueDate)}`;
  if (inv.status === 'sent') return inv.sentDate ? `Sent ${shortDay(inv.sentDate)}` : 'Sent';
  if (inv.status === 'paid') return inv.paidDate ? `Paid ${shortDay(inv.paidDate)}` : 'Paid';
  return undefined;
}

/**
 * One invoice (S07): amount and state in the hero card with the two things
 * you would do next, the time on it, the totals. Everything else is in "…".
 */
export function InvoiceDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const nouns = useBillingNouns();
  const invoice = useInvoice(id);
  const company = useCompanyInfo().data;
  const send = useSendInvoice();
  const followUp = useSendFollowUp();
  const setStatus = useSetInvoiceStatus();
  const discard = useDiscardInvoice();
  const paymentLink = useCreatePaymentLink();
  const canTakePayments = useCanTakePayments();
  const [menu, setMenu] = useState(false);
  const [confirm, setConfirm] = useState<{ title: string; action: SheetAction } | null>(null);
  const [editing, setEditing] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const inv = invoice.data;

  if (!inv) {
    return (
      <Screen title="Invoice" back="/invoices" backLabel="Invoices" large={false}>
        <Card className="flex flex-col gap-3">
          <Skeleton className="h-6 w-24 rounded-full" />
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-4 w-40" />
        </Card>
        <SkeletonList rows={3} />
      </Screen>
    );
  }

  const overdue = isOverdue(inv);
  const pdf = (share: boolean) => (share ? shareInvoicePdf : viewInvoicePdf)(inv, company, nouns.client);
  const entries = [...inv.entries].sort((a, b) => ((a.date ?? '') < (b.date ?? '') ? 1 : -1));
  const shown = showAll ? entries : entries.slice(0, FIRST);
  const timeTotal = inv.subtotal;
  const rate = inv.hourlyRate;
  const sendBlocked = Boolean(send.error && emailNotConfigured(send.error));

  const actions: SheetAction[] = [
    { label: 'View PDF', onSelect: () => void pdf(false) },
    { label: 'Share PDF', onSelect: () => void pdf(true) },
    ...(inv.status === 'sent' ? [{ label: 'Resend invoice', onSelect: () => send.mutate(inv.id) }] : []),
    ...(inv.status === 'draft' ? [{ label: 'Edit invoice', onSelect: () => setEditing(true) }] : []),
    { label: 'Duplicate', onSelect: () => setDuplicating(true) },
    ...(canTakePayments && inv.status !== 'paid'
      ? [
          inv.paymentLinkUrl
            ? { label: 'Copy payment link', onSelect: () => void copyToClipboard(inv.paymentLinkUrl!, 'Payment link') }
            : { label: 'Create payment link', onSelect: () => paymentLink.mutate(inv.id) }
        ]
      : []),
    ...(inv.status === 'draft'
      ? [
          {
            label: 'Delete draft',
            destructive: true,
            onSelect: () =>
              setConfirm({
                title: 'Its time entries become billable again.',
                action: {
                  label: 'Delete draft',
                  destructive: true,
                  onSelect: () => {
                    discard.mutate({ id: inv.id });
                    navigate('/invoices', { replace: true });
                  }
                }
              })
          }
        ]
      : inv.status === 'sent'
        ? [
            {
              label: 'Void invoice',
              destructive: true,
              onSelect: () =>
                setConfirm({
                  title: 'Voided invoices move to the archive. The client is not told.',
                  action: { label: 'Void invoice', destructive: true, onSelect: () => setStatus.mutate({ id: inv.id, status: 'archived' }) }
                })
            }
          ]
        : [])
  ];

  const billedTo = [inv.billTo?.name ? `Billed to ${inv.billTo.name}` : null, inv.status !== 'paid' && inv.dueDate ? `due ${shortDay(inv.dueDate)}` : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <Screen
      title={inv.invoiceNumber}
      back="/invoices"
      backLabel="Invoices"
      large={false}
      actions={
        <BarButton aria-label="More actions" onClick={() => setMenu(true)}>
          <MoreHorizontal className="size-6" />
        </BarButton>
      }
    >
      <Card className="flex flex-col gap-1.5 py-[18px]">
        <span className="self-start">
          <StatusPill status={inv.status} overdue={overdue} label={statusLabel(inv, overdue)} large />
        </span>
        <span className="tnum mt-1 text-[40px] leading-[46px] font-semibold tracking-[-0.02em]">{money(inv.totalAmount)}</span>
        <span className="text-body">{inv.clientName}</span>
        {billedTo && <span className="text-secondary text-[var(--text-muted)]">{billedTo}</span>}
        <div className="mt-3 grid grid-cols-2 gap-2">
          {inv.status === 'draft' && (
            <>
              <Button disabled={send.isPending} onClick={() => send.mutate(inv.id)}>
                {send.isPending ? 'Sending…' : 'Send invoice'}
              </Button>
              <Button variant="secondary" onClick={() => setEditing(true)}>
                Edit
              </Button>
            </>
          )}
          {inv.status === 'sent' && (
            <>
              <Button onClick={() => setStatus.mutate({ id: inv.id, status: 'paid' })}>Mark paid</Button>
              <Button variant="secondary" disabled={followUp.isPending} onClick={() => followUp.mutate(inv.id)}>
                Send reminder
              </Button>
            </>
          )}
          {inv.status !== 'draft' && inv.status !== 'sent' && (
            <>
              <Button onClick={() => void pdf(true)}>Share PDF</Button>
              <Button variant="secondary" onClick={() => void pdf(false)}>
                View PDF
              </Button>
            </>
          )}
        </div>
        {sendBlocked && <p className="mt-1 text-caption text-[var(--danger)]">Email is not set up on this server.</p>}
      </Card>

      {entries.length > 0 && (
        <Group header={`Time · ${h1(inv.totalHours)} h`} headerRight={`${moneyCompact(rate)} / h`}>
          {shown.map((e) => (
            <Row
              key={e.id}
              title={e.activityName ?? 'Work'}
              subtitle={[shortDay(e.date), e.misc].filter(Boolean).join(' · ')}
              trailing={
                <span className="flex flex-col items-end gap-0.5">
                  <span className="tnum text-body">{moneyCompact(e.hours * (e.hourlyRate ?? rate))}</span>
                  <span className="tnum text-secondary text-[var(--text-muted)]">{h1(e.hours)} h</span>
                </span>
              }
            />
          ))}
          {entries.length > FIRST && (
            <button type="button" onClick={() => setShowAll((s) => !s)} className="h-12 w-full text-secondary font-medium text-[var(--accent)] active:bg-[var(--pressed)]">
              {showAll ? 'Show fewer' : `Show all ${entries.length} entries`}
            </button>
          )}
        </Group>
      )}

      {inv.creditsAndFees.length > 0 && (
        <Group header="Credits & fees">
          {inv.creditsAndFees.map((c) => (
            <Row
              key={c.id}
              title={c.description}
              subtitle={c.kind === 'credit' ? 'Credit' : 'Fee'}
              trailing={<span className="tnum">{c.kind === 'credit' ? '−' : ''}{money(Math.abs(creditFeeAmount(c)))}</span>}
            />
          ))}
        </Group>
      )}

      <Group>
        <Row title={<span className="text-[var(--text-muted)]">Time</span>} trailing={<span className="tnum">{money(timeTotal)}</span>} />
        {inv.creditsTotal !== 0 && (
          <Row title={<span className="text-[var(--text-muted)]">Credits</span>} trailing={<span className="tnum">−{money(Math.abs(inv.creditsTotal))}</span>} />
        )}
        {inv.feesTotal !== 0 && (
          <Row title={<span className="text-[var(--text-muted)]">Fees</span>} trailing={<span className="tnum">{money(inv.feesTotal)}</span>} />
        )}
        <Row
          title={<span className="font-semibold">{inv.status === 'paid' ? 'Total paid' : 'Total due'}</span>}
          trailing={<span className="tnum font-semibold">{money(inv.totalAmount)}</span>}
        />
      </Group>

      <ActionSheet open={menu} onClose={() => setMenu(false)} title={`${inv.invoiceNumber} · ${inv.clientName ?? ''}`} actions={actions} />
      <ActionSheet open={confirm !== null} onClose={() => setConfirm(null)} title={confirm?.title} actions={confirm ? [confirm.action] : []} />
      {editing && <EditInvoiceSheet invoice={inv} open={editing} onClose={() => setEditing(false)} />}
      <NewInvoiceSheet open={duplicating} onClose={() => setDuplicating(false)} clientId={inv.clientId} />
    </Screen>
  );
}
